import { Sheet, SheetContent, SheetTitle } from '@shared/components/ui/sheet'
import { Badge } from '@shared/components/ui/badge'
import { Button } from '@shared/components/ui/button'
import { RotateCcw } from 'lucide-react'
import { Row, Section } from '@shared/components/ui/detail-sheet'
import { formatDateOnly } from '@shared/utils/date'
import type { Version } from '@shared/types/versioned-entity.types'

export interface VersionDetailSheetProps {
  open: boolean
  version: Version | null
  onClose: () => void
  onRevert?: (versionId: string) => void
}

const STATUS_VARIANT = {
  active: 'default' as const,
  draft: 'secondary' as const,
  archived: 'outline' as const,
}

const CHANGE_TYPE_VARIANT = {
  added: 'success' as const,
  modified: 'default' as const,
  removed: 'destructive' as const,
}

const CHANGE_TYPE_LABEL = {
  added: 'Added',
  modified: 'Modified',
  removed: 'Removed',
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'None'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function VersionDetailSheet({
  open,
  version,
  onClose,
  onRevert,
}: VersionDetailSheetProps) {
  if (!version) return null

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <SheetContent side="right" className="max-w-lg flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <SheetTitle className="text-lg">
              Version {version.version}
            </SheetTitle>
            <Badge variant={STATUS_VARIANT[version.status]} className="text-xs capitalize">
              {version.status}
            </Badge>
          </div>
          {version.description && (
            <p className="text-sm text-muted-foreground mt-1">{version.description}</p>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <Section title="Details">
            <Row label="Version" value={String(version.version)} />
            <Row label="Status" value={version.status} />
            <Row label="Created by" value={version.createdByName || version.createdBy} />
            <Row label="Created at" value={formatDateOnly(version.createdAt)} />
            {version.effectiveDate && (
              <Row label="Effective date" value={formatDateOnly(version.effectiveDate)} />
            )}
            {version.note && (
              <Row label="Note" value={version.note} />
            )}
          </Section>

          {version.changes.length > 0 && (
            <Section title={`Changes (${version.changes.length})`}>
              <div className="space-y-3">
                {version.changes.map((change, idx) => (
                  <div key={idx} className="rounded-md bg-muted/50 p-3 text-xs space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant={CHANGE_TYPE_VARIANT[change.changeType]} className="text-[10px] px-1.5 py-0">
                        {CHANGE_TYPE_LABEL[change.changeType]}
                      </Badge>
                      <span className="font-medium text-foreground">
                        {change.fieldLabel || change.field}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      {change.changeType === 'modified' && (
                        <span>
                          <span className="line-through">{formatValue(change.oldValue)}</span>
                          {' → '}
                          <span className="text-foreground font-medium">{formatValue(change.newValue)}</span>
                        </span>
                      )}
                      {change.changeType === 'added' && (
                        <span className="text-green-600">{formatValue(change.newValue)}</span>
                      )}
                      {change.changeType === 'removed' && (
                        <span className="text-red-600 line-through">{formatValue(change.oldValue)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex gap-2 justify-end">
          {onRevert && version.status !== 'active' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRevert(version.id)}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Revert to this version
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
